import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/solid'

export enum Type {
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

type ToastItem = {
  id: number
  message: string
  type: Type
}

type ToastContextValue = {
  toasts: ToastItem[]
  addToast: (message: string, type?: Type) => void
  removeToast: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const AUTO_DISMISS_MS = 5000

function ToastItemComponent({
  message,
  type,
  onDismiss,
}: {
  message: string
  type: Type
  onDismiss: () => void
}) {
  const iconConfig = {
    [Type.ERROR]: {
      icon: XCircleIcon,
      className: 'bg-red-50 text-red-400',
    },
    [Type.SUCCESS]: {
      icon: CheckCircleIcon,
      className: 'bg-green-50 text-green-400',
    },
  }
  const config = iconConfig[type] ?? {
    icon: ExclamationCircleIcon,
    className: 'bg-amber-50 text-amber-400',
  }
  const Icon = config.icon

  return (
    <div
      className="pointer-events-auto flex w-full max-w-sm rounded-lg bg-white shadow-lg ring-1 ring-black/5"
      role="alert"
    >
      <div className="flex flex-1 p-4">
        <div className="flex shrink-0">
          <span
            className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${config.className}`}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </span>
        </div>
        <div className="ml-3 flex flex-1 flex-col">
          <p className="text-sm font-medium text-gray-900">{message}</p>
        </div>
        <div className="ml-4 flex shrink-0">
          <button
            type="button"
            className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  )
}

function ToastContainer({ toasts, removeToast }: { toasts: ToastItem[]; removeToast: (id: number) => void }) {
  if (toasts.length === 0) return null
  return (
    <div
      className="fixed top-0 right-0 z-50 flex w-full flex-col items-end gap-3 p-4 sm:p-6"
      aria-live="assertive"
    >
      {toasts.map((t) => (
        <ToastItemComponent
          key={t.id}
          message={t.message}
          type={t.type}
          onDismiss={() => removeToast(t.id)}
        />
      ))}
    </div>
  )
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextIdRef = useRef(0)

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const addToast = useCallback((message: string, type: Type = Type.ERROR) => {
    const id = nextIdRef.current++
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => removeToast(id), AUTO_DISMISS_MS)
  }, [removeToast])

  const value = useMemo(
    () => ({ toasts, addToast, removeToast }),
    [toasts, addToast, removeToast]
  )

  const portal =
    typeof document !== 'undefined'
      ? createPortal(
          <ToastContainer toasts={toasts} removeToast={removeToast} />,
          document.body
        )
      : null

  return (
    <ToastContext.Provider value={value}>
      {children}
      {portal}
    </ToastContext.Provider>
  )
}

const useToast = () => {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return ctx.addToast
}

export default useToast
